import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, DeprecatedIconButton, Stack, Tooltip } from '@lyearn/ui';
import stopPropagation from '@/consts/stopPropogation';
import getTaskStatusIcon from '@/modules/tasks/views/TaskStasusesManager/utils/getStatusIcon';
import { svgSizeStyles } from '@/utils/styles';

import { useActionItemContext } from '../../context';
import useHovered from '../../hooks/useHovered';
import { ActionStatusProps } from '../../types';
import { disabledButtonStyles, linkButtonStyles } from '../ActionItem/styles';
import { isTaskEditable } from '../utils';
import StatusMenu from './StatusMenu';

const ActionStatus = ({ size = 20, className }: ActionStatusProps) => {
  const { t } = useTranslation();
  const {
    actionItem,
    actions,
    provider,
    isReadOnly,
    disableInteractiveTooltip = false,
  } = useActionItemContext();
  const { statusInfo } = actionItem;
  const [isPopoverOpen, { toggle }] = useToggle();
  const { hovered, onMouseMove } = useHovered();

  const onOpenPopover = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      stopPropagation(e);
      toggle();
    },
    [toggle],
  );

  const isDisabled = !isTaskEditable(provider);

  const StatusButton = statusInfo ? (
    <DeprecatedIconButton
      className={cn('h-30 w-30 xs:h-20 xs:w-20', svgSizeStyles(size / 10), linkButtonStyles, {
        [disabledButtonStyles]: isDisabled || isReadOnly,
      })}
      disabled={isDisabled || isReadOnly}
      shape="square"
      size="x-small"
      variant="ghost">
      {getTaskStatusIcon({
        category: statusInfo.category,
        icon: statusInfo.icon,
        size,
        progress: statusInfo.progress,
      })}
    </DeprecatedIconButton>
  ) : (
    <></>
  );

  if (!hovered) {
    return (
      <Stack className={className} onMouseMove={onMouseMove}>
        {StatusButton}
      </Stack>
    );
  }

  if (isDisabled || isReadOnly || !actions) {
    if (provider) {
      return (
        <Tooltip
          disableInteractive={disableInteractiveTooltip}
          placement="top"
          title={`${t('Common:SyncedWithProviderName', {
            providerName: provider.name || '',
          })}`}>
          <Stack className={className}>{StatusButton}</Stack>
        </Tooltip>
      );
    }

    return (
      <Tooltip disableInteractive={disableInteractiveTooltip} title={statusInfo?.name!}>
        <Stack className={className}>{StatusButton}</Stack>
      </Tooltip>
    );
  }

  if (!isPopoverOpen) {
    return (
      <Box className={className} onClick={onOpenPopover}>
        <Tooltip disableInteractive={disableInteractiveTooltip} title={statusInfo?.name!}>
          {StatusButton}
        </Tooltip>
      </Box>
    );
  }

  const { onStatusChange } = actions;

  return (
    <Box className={className} onClick={stopPropagation}>
      <StatusMenu
        open
        actionItemId={actionItem.id}
        statusInfo={statusInfo}
        onOpenChange={toggle}
        onStatusChange={onStatusChange}>
        <Tooltip disableInteractive={disableInteractiveTooltip} title={statusInfo?.name!}>
          {StatusButton}
        </Tooltip>
      </StatusMenu>
    </Box>
  );
};

export default React.memo(ActionStatus);
